import { enhancedApi } from '@store/generated/user-preferences';

/**
 * Feature-specific tag types for cache management
 */
const USER_PREFERENCES_TAG_TYPES = [
  'UserPreferences',
  'SuggestedActions',
  'ActionDefinitions',
] as const;

/**
 * User Preferences API - extends the generated OpenAPI client
 * This ensures we're using generated types and endpoints
 * while maintaining feature-specific customization
 */
export const userPreferencesApi = enhancedApi.enhanceEndpoints({
  addTagTypes: USER_PREFERENCES_TAG_TYPES,
  endpoints: {
    // Enhance generated endpoints with cache tags
    getV2ProviderUserPreferences: {
      providesTags: [{ type: 'UserPreferences', id: 'CURRENT' }],
    },

    putV2ProviderUserPreferences: {
      invalidatesTags: [{ type: 'UserPreferences', id: 'CURRENT' }],
      // Add optimistic updates for immediate UI feedback
      onQueryStarted: async ({ updateUserPreferencesRequest }, { dispatch, queryFulfilled }) => {
        // Optimistic update for user preferences
        const patchResult = dispatch(
          userPreferencesApi.util.updateQueryData(
            'getV2ProviderUserPreferences',
            undefined,
            draft => {
              // Merge the update request into the current preferences
              if (draft.preferences) {
                // Handle recording_preferences update
                if (updateUserPreferencesRequest.recording_preferences) {
                  draft.preferences.recording_preferences = {
                    ...draft.preferences.recording_preferences,
                    ...updateUserPreferencesRequest.recording_preferences,
                  };
                }
                // Handle document_preferences update
                if (updateUserPreferencesRequest.document_preferences) {
                  draft.preferences.document_preferences = {
                    ...draft.preferences.document_preferences,
                    ...updateUserPreferencesRequest.document_preferences,
                  };
                }
                // Handle subject update
                if (updateUserPreferencesRequest.subject !== undefined) {
                  draft.preferences.subject = updateUserPreferencesRequest.subject ?? undefined;
                }
                // Handle suggested_action_preferences update
                if (updateUserPreferencesRequest.suggested_action_preferences) {
                  draft.preferences.suggested_action_preferences = {
                    ...draft.preferences.suggested_action_preferences,
                    ...updateUserPreferencesRequest.suggested_action_preferences,
                  };
                }
              }
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch (error) {
          // Revert optimistic update on error
          patchResult.undo();
        }
      },
    },

    getV2ProviderUserPreferencesSuggestedActions: {
      providesTags: [{ type: 'SuggestedActions', id: 'LIST' }],
    },

    getV2ProviderUserPreferencesSuggestedActionsRegistry: {
      providesTags: [{ type: 'ActionDefinitions', id: 'LIST' }],
    },

    putV2ProviderUserPreferencesSuggestedActionsByActionId: {
      invalidatesTags: [
        { type: 'SuggestedActions', id: 'LIST' },
        { type: 'UserPreferences', id: 'CURRENT' },
      ],
      // Add optimistic updates for better UX
      onQueryStarted: async (
        { actionId, updateSuggestedActionPreferenceRequest },
        { dispatch, queryFulfilled },
      ) => {
        // Optimistic update for suggested actions
        const patchResult = dispatch(
          userPreferencesApi.util.updateQueryData(
            'getV2ProviderUserPreferencesSuggestedActions',
            undefined,
            draft => {
              if (draft.suggested_actions && draft.suggested_actions[actionId]) {
                draft.suggested_actions[actionId].enabled =
                  updateSuggestedActionPreferenceRequest.enabled ??
                  draft.suggested_actions[actionId].enabled;
              }
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch (error) {
          // Revert optimistic update on error
          patchResult.undo();
        }
      },
    },
  },
});

// Export hooks with user-friendly names
export const {
  // User preferences
  useGetV2ProviderUserPreferencesQuery: useGetUserPreferencesQuery,
  usePutV2ProviderUserPreferencesMutation: useUpdateUserPreferencesMutation,

  // Suggested actions
  useGetV2ProviderUserPreferencesSuggestedActionsQuery: useGetSuggestedActionsQuery,
  useGetV2ProviderUserPreferencesSuggestedActionsRegistryQuery: useGetActionDefinitionsQuery,
  usePutV2ProviderUserPreferencesSuggestedActionsByActionIdMutation:
    useUpdateSuggestedActionPreferenceMutation,
} = userPreferencesApi;
